/**
 * Inicios reservables de una cancha, con los intervalos completos disponibles.
 * Un inicio no disponible no implica que la cancha esté ocupada en ese instante:
 * puede quedar media hora libre sin espacio para un turno completo. Por eso no se
 * convierte la disponibilidad en una agenda de reservas ajenas (ADR-009).
 */

import type { CanchaId } from '@padel/domain';

import type { InicioDisponibilidad } from '@/infra/contratos';
import { finDeTurno, type FinDeTurno } from '@/publico/dominio/horario';

interface PropiedadesGrilla {
  readonly canchaId: CanchaId;
  readonly nombre: string;
  readonly descripcion: string;
  readonly estado: 'disponible' | 'proximamente';
  readonly inicios: readonly InicioDisponibilidad[];
  readonly canchaElegida: CanchaId | null;
  readonly horaElegida: string | null;
  readonly alElegir: (canchaId: CanchaId, localHora: string) => void;
}

function hayAlgunaDuracionLibre(inicio: InicioDisponibilidad): boolean {
  return inicio.opciones.some((opcion) => opcion.disponible);
}

function finalAlDiaSiguiente(fin: FinDeTurno | null): boolean {
  // Las 00:00 no ocupan el día siguiente, pero la salida pertenece a ese día.
  return fin !== null && (fin.cruzaMedianoche || fin.localHora === '00:00');
}

export function GrillaDeCancha({
  canchaId,
  nombre,
  descripcion,
  estado,
  inicios,
  canchaElegida,
  horaElegida,
  alElegir,
}: PropiedadesGrilla): React.JSX.Element {
  const idTitulo = `cancha-${canchaId}`;
  const iniciosLibres = inicios.filter(hayAlgunaDuracionLibre);
  const libres = iniciosLibres.length;
  const haySalidaAlDiaSiguiente = iniciosLibres.some((inicio) =>
    inicio.opciones.some(
      (opcion) =>
        opcion.disponible &&
        finalAlDiaSiguiente(finDeTurno(inicio.inicioUtc, opcion.duracionMinutos)),
    ),
  );

  return (
    <section
      aria-labelledby={idTitulo}
      className={`cancha${estado === 'proximamente' ? ' cancha--proximamente' : ''}`}
    >
      <header className="cancha__encabezado">
        <div className="cancha__titulo-fila">
          <h3 className="cancha__titulo" id={idTitulo}>
            {nombre}
          </h3>
          {estado === 'proximamente' ? (
            <span className="cancha__insignia">Proximamente</span>
          ) : null}
        </div>
        <p className="cancha__descripcion">{descripcion}</p>
        {estado === 'disponible' ? (
          <p className="cancha__resumen">
            {libres === 0
              ? 'Sin horarios libres'
              : `${String(libres)} ${libres === 1 ? 'horario de inicio disponible' : 'horarios de inicio disponibles'}`}
          </p>
        ) : null}
      </header>

      {estado === 'proximamente' ? (
        <div className="cancha__anuncio">
          <strong>Una nueva forma de jugar esta llegando.</strong>
          <p>Estamos preparando la cancha exterior. Muy pronto vas a poder reservarla aca.</p>
        </div>
      ) : inicios.length === 0 ? (
        <p className="cancha__vacia">No hay horarios para este dia en esta cancha.</p>
      ) : libres === 0 ? (
        <p className="cancha__vacia">No quedan turnos disponibles para este día en esta cancha.</p>
      ) : (
        <ul className="inicios">
          {iniciosLibres.map((inicio) => {
            const elegido = canchaElegida === canchaId && horaElegida === inicio.localHora;
            const idDetalle = `${idTitulo}-${inicio.localHora.replace(':', '')}-turnos`;

            return (
              <li key={inicio.inicioUtc}>
                <button
                  aria-label={`${inicio.localHora} en ${nombre}`}
                  aria-describedby={idDetalle}
                  aria-pressed={elegido}
                  className="inicio inicio--libre"
                  onClick={() => {
                    alElegir(canchaId, inicio.localHora);
                  }}
                  type="button"
                >
                  <span className="inicio__titulo">{inicio.localHora}</span>
                  <span className="inicio__opciones" id={idDetalle}>
                    {inicio.opciones
                      .filter((opcion) => opcion.disponible)
                      .map((opcion) => {
                        const fin = finDeTurno(inicio.inicioUtc, opcion.duracionMinutos);
                        return (
                          <span className="inicio__opcion" key={opcion.duracionMinutos}>
                            {opcion.duracionMinutos} min · hasta {fin?.localHora ?? '—'}
                            {finalAlDiaSiguiente(fin) ? (
                              <>
                                <sup aria-hidden="true">+1</sup>{' '}
                                <span className="solo-lectores">del día siguiente</span>
                              </>
                            ) : null}
                          </span>
                        );
                      })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {estado === 'disponible' && haySalidaAlDiaSiguiente ? (
        <p className="cancha__nota">+1: termina al día siguiente.</p>
      ) : null}
    </section>
  );
}
